import { Vue, Component, Prop, Ref, Watch, Setup } from "@wagich/vue-facing-decorator-metadata";
import ky from "ky";
import Mustache from "mustache";

import { render } from "./custom-form.html";
import type { FormField, Form } from "./models";
import { customFormRegistry } from "./registry";
import { CustomFormField } from "./custom-form-field";
import { TextField } from "./fields/text-field";
import { MultilineTextField } from "./fields/multiline-text-field";
import { GroupTitle } from "./fields/group-title";
import { CheckboxField } from "./fields/checkbox-field";
import { CheckboxGroupField } from "./fields/checkbox-group-field";
import { RadioGroupField } from "./fields/radio-group-field";
import { SelectField } from "./fields/select-field";
import { RichtextContent } from "./fields/richtext-content";
import { create, each, enforce, only, test, type Suite, type SuiteResult } from "vest";

customFormRegistry.register("Bluc.Forms.Models.FormGroupTitle, Bluc.Forms.Models", "GroupTitle", GroupTitle);
customFormRegistry.register("Bluc.Forms.Models.FormRichtextContent, Bluc.Forms.Models", "RichtextContent", RichtextContent);
customFormRegistry.register("Bluc.Forms.Models.TextFormField, Bluc.Forms.Models", "TextField", TextField, "");
customFormRegistry.register("Bluc.Forms.Models.MultilineTextFormField, Bluc.Forms.Models", "MultilineTextField", MultilineTextField, "");
customFormRegistry.register("Bluc.Forms.Models.CheckboxFormField, Bluc.Forms.Models", "CheckboxField", CheckboxField, false);
customFormRegistry.register("Bluc.Forms.Models.CheckboxGroupFormField, Bluc.Forms.Models", "CheckboxGroupField", CheckboxGroupField);
customFormRegistry.register("Bluc.Forms.Models.RadioGroupFormField, Bluc.Forms.Models", "RadioGroupField", RadioGroupField, []);
customFormRegistry.register("Bluc.Forms.Models.SelectFormField, Bluc.Forms.Models", "SelectField", SelectField, []);
customFormRegistry.registerAsync("Bluc.Forms.Models.UploadFormField, Bluc.Forms.Models", "UploadField",
	() => import("./fields/upload-field").then(m => m.UploadField));
customFormRegistry.registerAsync("Transfair.Models.EventRegistrationFormField, Transfair", "EventRegistrationField",
	() => import("./fields/event-registration-field").then(m => m.EventRegistrationField));

enum State {
	Loading = "loading",
	Ready = "ready",
	ActionSuccess = "actionSuccess",
	ActionError = "actionError",
	Error = "error",
}

type ServerValidationState = { [fieldName: string]: string[]; };

@Component({
	render,
	components: customFormRegistry.getComponents()
})
export class CustomForm extends Vue {
	form: Form | null = null;
	values: any = {};
	validator: Suite<string, string, (data: any, serverErrors: ServerValidationState, field?: string) => void> = null!;
	validations: SuiteResult<string, string> = null!;
	state: State = State.Loading;
	serverModelState: ServerValidationState = {};
	isInitialized: boolean;

	@Ref("fieldComponents") fieldComponents: CustomFormField[];
	@Prop({ required: true }) formId: string;
	@Prop({ type: Object, default: {} }) metadata: { [key: string]: any };

	get successMessageHtml(): string {
		return Mustache.render(this.form?.successMessageHtml ?? "", Object.assign({}, this.metadata, this.values));
	}
	get errorMessageHtml(): string {
		return Mustache.render(this.form?.errorMessageHtml ?? "", Object.assign({}, this.metadata, this.values));
	}
	get textHtml(): string {
		return Mustache.render(this.form?.textHtml ?? "", this.metadata);
	}
	get submitButtonText(): string {
		if (this.form != null && this.form.submitButtonText != null && this.form.submitButtonText.length > 0) {
			return this.form.submitButtonText;
		}
		return "Absenden";
	}
	get privacyPolicyUrl(): string | undefined {
		return this.form?.privacyPolicyUrl;
	}

	async mounted() {
		try {
			let response = await ky.get(`/api/custom-form/${this.formId}`);
			let form = await response.json<Form>();

			this.initializeValueDictionary(form);
			this.initializeValidation(form);
			this.form = form;

			this.state = State.Ready;

			try {
				plausible("Formular geöffnet", { props: { "Formular": form.title } });
			} catch { }
		} catch (e) {
			this.state = State.Error;
		}
	}

	getComponentName(field: FormField): string | undefined {
		return customFormRegistry.getComponentName(field.fieldType);
	}

	onFieldChanged(field: FormField) {
		if (this.serverModelState[field.name] !== undefined) {
			delete this.serverModelState[field.name];
		}
		this.validate(field.name);
	}

	validate(field?: string): boolean {
		if (this.form == null || this.fieldComponents == null) {
			return true;
		}

		for (let component of this.fieldComponents) {
			if (field == null || component.field.name === field) {
				component.setDirty();
			}
		}
		this.validations = this.validator(this.values, this.serverModelState, field);

		return this.validations.isValid();
	}

	async submit() {
		this.serverModelState = {};
		if (!this.validate()) {
			return;
		}

		this.state = State.Loading;

		try {
			this.ensureMetadata();

			let payload = {
				transactionId: this.form?.transactionId,
				data: this.values,
				metadata: this.metadata,
			};

			let response = await ky.post(`/api/custom-form/${this.formId}`, {
				json: payload,
				throwHttpErrors: false
			});

			if (response.ok) {
				this.state = State.ActionSuccess;

				try {
					plausible("Formular abgeschickt", { props: { "Formular": this.form!.title } });
				} catch { }
			}
			else if (response.status === 400) {
				this.state = State.Ready;
				this.serverModelState = await response.json();
				this.validate();

			} else {
				this.state = State.ActionError;
			}

		} catch (e) {
			this.state = State.ActionError;
		}
	}

	reset() {
		this.state = State.Ready;
		if (this.form != null) {
			this.initializeValueDictionary(this.form);
			this.initializeValidation(this.form);
		}
	}

	close() {
		this.$emit("close");
	}

	private ensureMetadata() {
		// use readable, german keys because this will be output 1:1 to the user
		// keys should be valid mustache variable names (no spaces)

		this.metadata["QuelleUrl"] = window.location.href;

		let title = document.title;
		// remove standard suffix
		if (title.endsWith("| TRANSfair")) {
			title = title.slice(0, -12);
		}
		this.metadata["Quelle"] = title;
	}

	private initializeValidation(form: Form) {
		this.validator = create((data: any, serverValidationState: ServerValidationState, fieldName: string | undefined = undefined) => {
			only(fieldName);

			for (let component of this.fieldComponents) {
				component.createValidator(data);
			}
			// wrap each server-side error in an always-failing test to display it in the UI
			each(Object.entries(serverValidationState), ([serverFieldName, errors]) => {
				test(serverFieldName, errors.join(", "), () => {
					enforce(false).equals(true);
				});
			});
		});
		this.validations = this.validator.get();
	}

	private initializeValueDictionary(form: Form) {
		for (let field of form.fields) {
			if (field.name != null) {
				let component = customFormRegistry.getComponent(field.fieldType);
				this.values[field.name] = component?.defaultValue;
			}
		}
	}
}
